'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import MarketingNav from '@/app/marketing/components/MarketingNav';
import MarketingFooter from '@/app/marketing/components/MarketingFooter';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'For solo traders and small shops',
    monthly: 99,
    annual: 949,
    color: 'border-violet-500/30',
    accent: 'text-violet-400',
    bg: 'bg-violet-500/8',
    btn: 'bg-violet-600 hover:bg-violet-500',
    highlight: false,
    features: [
      '500 inventory items',
      '1 user (Owner role)',
      'Rapaport price calculator',
      'QR code scanning',
      'Basic P&L reporting',
      'GIA certificate link',
      'Email support',
      '14-day free trial',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For growing teams and active traders',
    monthly: 249,
    annual: 2390,
    color: 'border-amber-500/50',
    accent: 'text-amber-400',
    bg: 'bg-amber-500/8',
    btn: 'bg-amber-500 hover:bg-amber-400 text-[#0a0e1a]',
    highlight: true,
    features: [
      '5,000 inventory items',
      'Up to 3 users (all roles)',
      'Full CRM + deal pipeline',
      'Email automation',
      'Advanced analytics',
      'Memo & hold management',
      'Appraisal module',
      'Priority support',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large operations and multi-location',
    monthly: 599,
    annual: 5750,
    color: 'border-indigo-500/30',
    accent: 'text-indigo-400',
    bg: 'bg-indigo-500/8',
    btn: 'bg-indigo-600 hover:bg-indigo-500',
    highlight: false,
    features: [
      'Unlimited inventory',
      'Up to 10 users',
      'Multi-location support',
      'API access',
      'Dedicated account manager',
      'Custom onboarding',
      'SLA guarantee',
      'White-label option',
    ],
  },
];

const COMPARISON_ROWS = [
  { feature: 'Inventory items', starter: '500', professional: '5,000', enterprise: 'Unlimited' },
  { feature: 'Users', starter: '1', professional: '3', enterprise: '10' },
  { feature: 'Rapaport calculator', starter: true, professional: true, enterprise: true },
  { feature: 'QR scanning', starter: true, professional: true, enterprise: true },
  { feature: 'GIA verification', starter: true, professional: true, enterprise: true },
  { feature: 'Full CRM', starter: false, professional: true, enterprise: true },
  { feature: 'Deal pipeline', starter: false, professional: true, enterprise: true },
  { feature: 'Email automation', starter: false, professional: true, enterprise: true },
  { feature: 'Appraisal module', starter: false, professional: true, enterprise: true },
  { feature: 'Advanced analytics', starter: false, professional: true, enterprise: true },
  { feature: 'Multi-location', starter: false, professional: false, enterprise: true },
  { feature: 'API access', starter: false, professional: false, enterprise: true },
  { feature: 'Dedicated manager', starter: false, professional: false, enterprise: true },
  { feature: 'White-label', starter: false, professional: false, enterprise: true },
];

function CheckIcon({ yes }: { yes: boolean }) {
  if (yes) {
    return (
      <svg
        className="w-5 h-5 text-emerald-400 mx-auto"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  return <span className="block w-4 h-0.5 bg-[#374151] mx-auto rounded-full" />;
}

export default function PricingPage() {
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');

  return (
    <div
      className="min-h-screen bg-[#0a0e1a] text-[#e5e7eb]"
      style={{ fontFamily: 'var(--font-plus-jakarta-sans), system-ui, sans-serif' }}
    >
      <MarketingNav />
      <main id="main-content">
        {/* Hero */}
        <section className="pt-28 pb-16 px-4 sm:px-6 text-center relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-amber-500/5 blur-[100px]" />
          </div>
          <div className="relative z-10 max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/8 text-amber-300 text-xs font-medium mb-6">
              Simple, Transparent Pricing
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
              Plans for Every Diamond Business
            </h1>
            <p className="text-[#9ca3af] text-lg mb-8">
              No hidden fees. No long-term contracts. 14-day free trial on every plan.
            </p>

            {/* Billing toggle */}
            <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[#111827] border border-white/8">
              <button
                onClick={() => setBilling('monthly')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  billing === 'monthly'
                    ? 'bg-white/10 text-white'
                    : 'text-[#9ca3af] hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBilling('annual')}
                className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  billing === 'annual'
                    ? 'bg-white/10 text-white'
                    : 'text-[#9ca3af] hover:text-white'
                }`}
              >
                Annual
                <span className="text-xs bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full font-semibold">
                  Save 20%
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="pb-20 px-4 sm:px-6">
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl border p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 ${
                  plan.highlight
                    ? `${plan.color} bg-gradient-to-b from-amber-500/8 to-[#111827] shadow-xl shadow-amber-500/10`
                    : `${plan.color} bg-[#111827]`
                }`}
              >
                {plan.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 rounded-full bg-amber-500 text-[#0a0e1a] text-xs font-bold shadow-lg">
                      ★ Most Popular
                    </span>
                  </div>
                )}

                <div className={`text-xs font-bold uppercase tracking-widest ${plan.accent} mb-2`}>
                  {plan.name}
                </div>
                <p className="text-xs text-[#9ca3af] mb-5">{plan.tagline}</p>

                <div className="mb-6">
                  <span className="text-4xl font-black text-white">
                    ${billing === 'monthly' ? plan.monthly : plan.annual}
                  </span>
                  <span className="text-[#9ca3af] text-sm ml-1">
                    {billing === 'monthly' ? '/month' : '/year'}
                  </span>
                  {billing === 'annual' && (
                    <p className="text-xs text-emerald-400 mt-1">
                      Save ${plan.monthly * 12 - plan.annual} vs monthly
                    </p>
                  )}
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat} className="flex items-start gap-2.5 text-sm text-[#d1d5db]">
                      <svg
                        className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {feat}
                    </li>
                  ))}
                </ul>

                <Link
                  href="/demo"
                  className={`w-full py-3 rounded-xl text-sm font-bold text-center transition-colors ${plan.btn} ${plan.highlight ? '' : 'text-white'}`}
                >
                  Start Free Trial
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Add-ons */}
        <section className="py-16 px-4 sm:px-6 bg-[#0d1120] border-y border-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">Add-Ons</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                {
                  name: 'Extra Users',
                  price: '$49/user/month',
                  desc: 'Add more team members to any plan.',
                },
                {
                  name: 'Extra Inventory',
                  price: '$29/1,000 items',
                  desc: 'Scale your inventory beyond plan limits.',
                },
                {
                  name: 'Custom Integrations',
                  price: 'From $199/month',
                  desc: 'Connect to your accounting or e-commerce platform.',
                },
              ].map((addon) => (
                <div key={addon.name} className="rounded-xl border border-white/8 bg-[#111827] p-5">
                  <h3 className="text-sm font-bold text-white mb-1">{addon.name}</h3>
                  <p className="text-amber-400 text-sm font-semibold mb-2">{addon.price}</p>
                  <p className="text-xs text-[#9ca3af]">{addon.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature comparison table */}
        <section className="py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-10">
              Compare All Features
            </h2>
            <div className="rounded-2xl border border-white/8 overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-4 bg-[#0d1120] border-b border-white/5">
                <div className="p-4 text-xs font-semibold text-[#9ca3af] uppercase tracking-wide">
                  Feature
                </div>
                {PLANS.map((p) => (
                  <div
                    key={p.id}
                    className={`p-4 text-center text-xs font-bold uppercase tracking-wide ${p.accent}`}
                  >
                    {p.name}
                  </div>
                ))}
              </div>
              {COMPARISON_ROWS.map((row, i) => (
                <div
                  key={row.feature}
                  className={`grid grid-cols-4 border-b border-white/4 ${i % 2 === 0 ? 'bg-[#111827]' : 'bg-[#0f1623]'}`}
                >
                  <div className="p-4 text-sm text-[#d1d5db]">{row.feature}</div>
                  <div className="p-4 text-center text-sm text-[#9ca3af]">
                    {typeof row.starter === 'boolean' ? (
                      <CheckIcon yes={row.starter} />
                    ) : (
                      row.starter
                    )}
                  </div>
                  <div className="p-4 text-center text-sm text-[#9ca3af]">
                    {typeof row.professional === 'boolean' ? (
                      <CheckIcon yes={row.professional} />
                    ) : (
                      row.professional
                    )}
                  </div>
                  <div className="p-4 text-center text-sm text-[#9ca3af]">
                    {typeof row.enterprise === 'boolean' ? (
                      <CheckIcon yes={row.enterprise} />
                    ) : (
                      row.enterprise
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 sm:px-6 bg-[#0d1120] border-t border-white/5">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                {
                  q: 'Can I switch plans later?',
                  a: 'Yes. Upgrade or downgrade at any time. Changes take effect on your next billing cycle.',
                },
                {
                  q: 'Is there a free trial?',
                  a: 'All plans include a 14-day free trial. No credit card required to start.',
                },
                {
                  q: 'Do you support Thai Baht (THB)?',
                  a: 'Yes. Glanz CRM supports both THB and USD with a live currency toggle.',
                },
                {
                  q: 'What happens to my data if I cancel?',
                  a: 'You can export all your data at any time. We retain it for 30 days after cancellation.',
                },
              ].map((faq) => (
                <div key={faq.q} className="rounded-xl border border-white/8 bg-[#111827] p-5">
                  <h3 className="text-sm font-semibold text-white mb-2">{faq.q}</h3>
                  <p className="text-sm text-[#9ca3af]">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 sm:px-6 text-center">
          <div className="max-w-xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-4">Start Your 14-Day Free Trial</h2>
            <p className="text-[#9ca3af] mb-6">
              No credit card required. Full access to all Professional features.
            </p>
            <Link
              href="/demo"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-[#0a0e1a] font-bold text-sm shadow-xl shadow-amber-500/20 transition-all hover:-translate-y-0.5"
            >
              Book a Demo & Start Trial
            </Link>
          </div>
        </section>
      </main>
      <MarketingFooter />
    </div>
  );
}
